import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { user } from 'shared/redux/selectors';
import {
  base64ToObject,
  objectToBaseB64,
  parseQueryString,
} from 'shared/utils';
import { getUrl } from 'shared/utils/urls';

export const protectedResources = {
  ATG_URL_BET: {
    subscriptionRequired: true,
    freemium: true,
    key: 'atgUrlBet',
  },
  ATG_JACKPOT_BET: {
    subscriptionRequired: true,
    freemium: true,
    key: 'atgJackpotBet',
  },
  ATG_HIHGLIGHT_BET: {
    subscriptionRequired: true,
    freemium: true,
    key: 'atgHighlightBet',
  },
  ATG_CALENDAR_BET: {
    subscriptionRequired: true,
    freemium: true,
    key: 'atgCalendarBet',
  },
  ATG_TREND_BET: {
    subscriptionRequired: true,
    freemium: true,
    key: 'atgTrendBet',
  },
  ATG_OPEN_ARCHIVE: {
    subscriptionRequired: true,
    freemium: true,
    key: 'atgOpenArchive',
  },
  CREATE_WIZ: { subscriptionRequired: true, freemium: true, key: 'createWiz' },
  SPORT_BET: { subscriptionRequired: false, key: 'sportBet' },
  SPORT_OPEN_ARCHIVE: { subscriptionRequired: false, key: 'sportOpenArchive' },
  MONITOR_HORSES: { subscriptionRequired: true, key: 'monitorHorses' },
};

export const PaywallContext = createContext({});

export const usePaywallContext = () => {
  const context = useContext(PaywallContext);
  if (context == null) {
    throw new Error(
      'usePaywallContext must be used within PaywallContext.Provider',
    );
  }
  return context;
};

export const parseLocationForEncodedAction = location => {
  try {
    const { action } = parseQueryString(location.search);
    const decodedAction =
      action != null ? base64ToObject(decodeURIComponent(action)) : null;

    if (decodedAction == null) {
      return null;
    }

    const resource = Object.values(protectedResources).find(
      value => value.key in decodedAction,
    );

    if (resource == null) {
      return;
    }

    const params = decodedAction[resource.key];

    return { resource, params };
  } catch {
    return null;
  }
};

export const encodeAction = (resource, params) => {
  const obj = { [resource.key]: params };
  return objectToBaseB64(obj);
};

export const redirectToLoginWithEncodedAction = (resource, params) => {
  window.location.href = getUrl.login(
    `${window.location.pathname}?action=${encodeAction(resource, params)}`,
  );
};

export const useProtectedResources = (
  authorizedCallback,
  allowCancel = true,
) => {
  const history = useHistory();
  const location = useLocation();
  const isLoggedIn = useSelector(user.isLoggedIn);
  const isSubscriber = useSelector(state => user.hasSubscription(state));
  const [paywallContextValue, setPaywallContextValue] = useState({});

  const cancelPaywall = useCallback(() => {
    history.replace(location.pathname);
    setPaywallContextValue({});
  }, [history, location.pathname]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    const decoded = parseLocationForEncodedAction(location);

    if (decoded == null) {
      setPaywallContextValue({});
      return;
    }

    const { resource, params } = decoded;

    if (
      isSubscriber ||
      !resource.subscriptionRequired ||
      (ALLOW_FREEMIUM_HORSE_BETTING && resource.freemium)
    ) {
      history.replace(location.pathname);
      authorizedCallback(resource, params);
    } else {
      setPaywallContextValue({
        resource,
        params,
        cancelPaywall: allowCancel ? cancelPaywall : undefined,
      });
    }
  }, [
    allowCancel,
    authorizedCallback,
    cancelPaywall,
    history,
    isLoggedIn,
    isSubscriber,
    location,
  ]);

  const eventHandler = useCallback(
    (boundResource, eventData) => {
      const params =
        eventData == null || 'nativeEvent' in eventData ? null : eventData;
      if (!isLoggedIn) {
        redirectToLoginWithEncodedAction(boundResource, params);
      } else if (boundResource.subscriptionRequired && !isSubscriber) {
        history.replace(
          `${location.pathname}?action=${encodeAction(boundResource, params)}`,
        );
      } else {
        authorizedCallback(boundResource, params);
      }
    },
    [authorizedCallback, history, isLoggedIn, isSubscriber, location.pathname],
  );

  const createAtgBetFromUrl = useCallback(
    eventData => eventHandler(protectedResources.ATG_URL_BET, eventData),
    [eventHandler],
  );

  const createAtgBetFromJackpot = useCallback(
    eventData => eventHandler(protectedResources.ATG_JACKPOT_BET, eventData),
    [eventHandler],
  );

  const createAtgBetFromHighlight = useCallback(
    eventData => eventHandler(protectedResources.ATG_HIHGLIGHT_BET, eventData),
    [eventHandler],
  );

  const createAtgBetFromCalendar = useCallback(
    eventData => eventHandler(protectedResources.ATG_CALENDAR_BET, eventData),
    [eventHandler],
  );

  const createAtgBetFromTrend = useCallback(
    eventData => eventHandler(protectedResources.ATG_TREND_BET, eventData),
    [eventHandler],
  );

  const initiateWizBet = useCallback(
    eventData => eventHandler(protectedResources.CREATE_WIZ, eventData),
    [eventHandler],
  );

  const openAtgArchiveDialog = useCallback(
    eventData => eventHandler(protectedResources.ATG_OPEN_ARCHIVE, eventData),
    [eventHandler],
  );

  const createSportBet = useCallback(
    eventData => eventHandler(protectedResources.SPORT_BET, eventData),
    [eventHandler],
  );

  const openSportArchiveDialog = useCallback(
    eventData => eventHandler(protectedResources.SPORT_OPEN_ARCHIVE, eventData),
    [eventHandler],
  );

  const monitorHorses = useCallback(
    eventData => eventHandler(protectedResources.MONITOR_HORSES, eventData),
    [eventHandler],
  );

  return {
    createAtgBetFromUrl,
    createAtgBetFromJackpot,
    createAtgBetFromHighlight,
    createAtgBetFromCalendar,
    createAtgBetFromTrend,
    initiateWizBet,
    openAtgArchiveDialog,
    createSportBet,
    openSportArchiveDialog,
    monitorHorses,
    paywallContextValue,
  };
};
